import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ChevronLeft} from 'lucide-react';
import axios, {AxiosRequestConfig} from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import LabResultsHeader from '../components/results/LabResultsHeader';
import ResultsView from '../components/results/ResultsView';
import BinaryResult from '../components/results/BinaryResult';
import MembershipLock from '../components/common/MembershipLock';
import {getNameString} from '../utils/Test';
import settings from '../settings';
import type {Test} from '../types/api';

type SingleResultContentProps = {
    result: Test;
    realResult: Test | null;
    isMember: boolean;
    token?: string;
    backTo: string;
    backLabel: string;
};

type MsSaveBlobNavigator = Navigator & {msSaveOrOpenBlob?: (blob: Blob) => void};

const SingleResultContent: React.FC<SingleResultContentProps> = ({
    result,
    realResult,
    isMember,
    token,
    backTo,
    backLabel,
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [, setMenuVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadFailed, setDownloadFailed] = useState(false);
    const [aboutTestVisible, setAboutTestVisible] = useState(false);

    // Initialize filter from URL parameter if present
    const statusParam = searchParams.get('status');
    const [filterBy, setFilterBy] = useState(
        statusParam && ['normal', 'borderline', 'critical'].includes(statusParam) ? statusParam : 'all'
    );

    const [sortBy, setSortBy] = useState('date');
    const [viewMode, setViewMode] = useState<'panel' | 'biomarker'>('panel');
    const menuRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const createdDateString = result?.createdDate
        ? new Date(result.createdDate).toLocaleDateString('en-US', {timeZone: 'UTC'})
        : 'N/A';
    const statusDateString = result?.statusDate
        ? new Date(result.statusDate).toLocaleDateString('en-US', {timeZone: 'UTC'})
        : 'N/A';

    const oidPrefix = result?.oid?.substring(result.oid.length - 10, result.oid.length - 8);
    const hasMultipleResults = ['BW', 'CF', 'BT', 'AL', 'ST'].includes(oidPrefix || '');
    const isPanel = ['BW', 'AL'].includes(oidPrefix || '');
    const hasAboutTest = ['AL', 'AB', 'CV', 'RA', 'ST', 'RP'].includes(oidPrefix || '');

    // Determine if this test supports panel/biomarker views and filtering
    const supportsPanelView = result?.resultType === 'allergy' || result?.resultType === 'blood' || isPanel;

    const testNameString = result ? getNameString(result) : '';

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement;
            if (target?.id === 'context-menu-overlay' || target?.id === 'context-menu-close') {
                setMenuVisible(false);
            }
            if (
                (overlayRef.current && overlayRef.current.contains(target)) ||
                (menuRef.current && menuRef.current.contains(target)) ||
                (target && target.id === 'menu-button-image')
            ) {
                return;
            }
            setMenuVisible(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDownload = async () => {
        if (downloading) return;

        setDownloading(true);

        try {
            const headers: Record<string, string> = {'Content-Type': 'application/json'};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            const requestOptions: AxiosRequestConfig = {
                headers,
                responseType: 'blob',
            };

            // Always use real elis_id for PDF download, even when showing dummy data
            const pdfElisId = realResult?.elis_id || result.elis_id;
            const res = await axios.post(`${settings.apiURL}/api/order/pdf`, JSON.stringify({id: pdfElisId}), requestOptions);

            if (res.status !== 200) {
                setDownloading(false);
                setDownloadFailed(true);
                console.error('PDF download failed with status:', res.status);
                return;
            }

            // Download retrieved PDF
            const filename = res.headers['content-disposition']?.split('=')[1];
            const blob = res.data;
            const newBlob = new Blob([blob], {type: 'application/pdf'});

            const msSaveOrOpenBlob = (window.navigator as MsSaveBlobNavigator).msSaveOrOpenBlob;
            if (msSaveOrOpenBlob) {
                msSaveOrOpenBlob(newBlob);
                // Reset downloading state after download completes
                setDownloading(false);
            } else {
                const objUrl = window.URL.createObjectURL(newBlob);
                const link = document.createElement('a');
                link.href = objUrl;
                link.download = filename || 'Report';
                link.click();

                setTimeout(() => {
                    window.URL.revokeObjectURL(objUrl);
                    // Reset downloading state after download completes
                    setDownloading(false);
                }, 250);
            }
        } catch (error) {
            // Download error occurred
            console.error('PDF download error:', error);
            setDownloading(false);
            setDownloadFailed(true);
        }
    };

    return (
        <DashboardLayout isMember={isMember}>
            {/* Failure and About Test Modals */}
            {(downloadFailed || aboutTestVisible) && (
                <div
                    ref={overlayRef}
                    id="context-menu-overlay"
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={(ev) => {
                        if ((ev.target as HTMLElement).id === 'context-menu-overlay') {
                            setDownloadFailed(false);
                            setAboutTestVisible(false);
                        }
                    }}>
                    {downloadFailed && (
                        <div className="bg-white p-6 rounded-lg max-w-md mx-4">
                            <h3 className="text-lg font-semibold text-red-600 mb-2">Download Failed</h3>
                            <p className="text-gray-600 mb-4">Unable to download the PDF. Please try again later.</p>
                            <button
                                onClick={() => setDownloadFailed(false)}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 cursor-pointer">
                                Close
                            </button>
                        </div>
                    )}
                    {aboutTestVisible && (
                        <div className="bg-white p-6 rounded-lg max-w-md mx-4">
                            <h3 className="text-lg font-semibold text-mld-text-primary mb-2">About This Test</h3>
                            <p className="text-gray-600 mb-4">Information about {testNameString}.</p>
                            <button
                                onClick={() => setAboutTestVisible(false)}
                                className="px-4 py-2 bg-mld-primary text-white rounded hover:bg-mld-primary/90 cursor-pointer">
                                Close
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Main content area - full width */}
            <div className={`relative py-3 lg:py-6 ${!isMember ? 'pointer-events-none select-none filter blur-sm' : ''}`}>
                <div className="px-3 lg:px-6">
                    {/* Back button */}
                    <div className="mb-4">
                        <button
                            onClick={() => navigate(backTo)}
                            className="flex items-center text-mld-primary hover:text-mld-primary/80 transition-colors cursor-pointer">
                            <ChevronLeft className="h-5 w-5 mr-1" />
                            {backLabel}
                        </button>
                    </div>

                    {/* Page Title and Controls - Not in a panel */}
                    <LabResultsHeader
                        onFilterChange={setFilterBy}
                        onSortChange={setSortBy}
                        onViewChange={setViewMode}
                        onDownload={handleDownload}
                        downloading={downloading}
                        hasAboutTest={hasAboutTest}
                        onAboutTest={() => setAboutTestVisible(true)}
                        currentFilter={filterBy}
                        currentSort={sortBy}
                        showControls={supportsPanelView}
                    />
                </div>

                <div className="px-3 lg:px-6">
                    {/* Date Information */}
                    {(result.firstName || result.lastName) && (
                        <div className="mb-4 lg:mb-6 text-xs lg:text-sm text-gray-600 pb-4 lg:pb-6 border-b border-gray-300">
                            {`Collected on: ${createdDateString === '1/1/2001' ? 'N/A' : createdDateString} - Received on: ${createdDateString === '1/1/2001' ? 'N/A' : createdDateString} - Results generated on: ${statusDateString === '1/1/2001' ? 'N/A' : statusDateString}`}
                        </div>
                    )}
                </div>

                {/* Results Display */}
                <div className="px-3 lg:px-6 space-y-4 lg:space-y-8">
                    {/* Render based on view mode */}
                    {result.resultType === 'allergy' || result.resultType === 'blood' || isPanel ? (
                        <ResultsView
                            result={result}
                            test_type={result.resultType || (oidPrefix === 'BW' ? 'blood' : oidPrefix === 'AL' ? 'allergy' : '')}
                            sortBy={sortBy}
                            filterBy={filterBy}
                            recommendations={result.recommendations}
                            viewMode={viewMode}
                            onClearFilters={() => {
                                setFilterBy('all');
                                setSortBy('newest');
                            }}
                        />
                    ) : result.resultType && result.resultType === 'binary' ? (
                        <BinaryResult result={result} hasMultipleResults={hasMultipleResults} />
                    ) : (
                        <BinaryResult result={result} hasMultipleResults={hasMultipleResults} />
                    )}
                </div>
            </div>

            {/* Membership lock overlay for non-members - with PDF download */}
            {!isMember && <MembershipLock showPdfDownload={true} onPdfDownload={() => handleDownload()} />}
        </DashboardLayout>
    );
};

export default SingleResultContent;
